import * as React from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { usePermissions } from '@/auth/PermissionsProvider';
import { useAgencyId } from '@/data/rpc';
import { toActivityItem, type ActivityItem } from '@/lib/activity';
import { userChannel } from '@/lib/channels';
import { parsePublication, pollDelay } from '@/lib/realtime';
import { useRealtime } from '@/realtime/RealtimeProvider';

/**
 * The bell's Activity tab (change spec 2026-10-02/activity-feed, design 9.2): `activity_feed` (newest 30)
 * and `activity_unread_count` for the CURRENT agency. Re-read:
 *   * on an `activity.new` hint on `user:<self>` for this agency (the 0266 triggers send one per team
 *     action to the people who may see it), debounced; every other hint on that channel is ignored here;
 *   * on every (re)subscribe of that channel (a first subscription recovers nothing);
 *   * by the fallback poll and when the tab comes back, like the For me feed.
 * Without `activity.read` the hook reads nothing at all and reports `enabled: false`.
 */

export const ACTIVITY_BELL_LIMIT = 30;
const HINT_DEBOUNCE_MS = 400;

export interface ActivityState {
  enabled: boolean;
  status: 'loading' | 'ready' | 'error';
  items: ActivityItem[];
  unreadCount: number;
  reload(): void;
  markRead(untilIso: string): Promise<void>;
}

function isVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

export function useActivity(): ActivityState {
  const { client, user } = useAuth();
  const agencyId = useAgencyId();
  const { can } = usePermissions();
  const enabled = !!agencyId && can('activity.read');
  const { state: rtState, subscribe } = useRealtime();
  const userId = user?.id ?? null;

  const [status, setStatus] = React.useState<ActivityState['status']>('loading');
  const [items, setItems] = React.useState<ActivityItem[]>([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const seq = React.useRef(0);

  const read = React.useCallback(async () => {
    if (!enabled || !agencyId) return;
    const mine = ++seq.current;
    const [feed, count] = await Promise.all([
      client.rpc('activity_feed', { p_agency: agencyId, p_limit: ACTIVITY_BELL_LIMIT }),
      client.rpc('activity_unread_count', { p_agency: agencyId }),
    ]);
    if (mine !== seq.current) return;
    if (feed.error || count.error) {
      console.warn('useActivity: activity read failed', feed.error ?? count.error);
      setStatus((s) => (s === 'ready' ? s : 'error'));
      return;
    }
    const rows = (feed.data ?? []) as Record<string, unknown>[];
    setItems(rows.map(toActivityItem).filter((n): n is ActivityItem => n !== null));
    setUnreadCount(Number(count.data ?? 0) || 0);
    setStatus('ready');
  }, [client, agencyId, enabled]);

  const readRef = React.useRef(read);
  readRef.current = read;

  const debounce = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const reload = React.useCallback(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      debounce.current = null;
      void readRef.current();
    }, HINT_DEBOUNCE_MS);
  }, []);
  React.useEffect(
    () => () => {
      if (debounce.current) clearTimeout(debounce.current);
    },
    [],
  );

  // A new agency (or the permission turning on or off) starts from nothing.
  React.useEffect(() => {
    setItems([]);
    setUnreadCount(0);
    setStatus('loading');
    void read();
  }, [read]);

  const agencyRef = React.useRef(agencyId);
  agencyRef.current = agencyId;
  React.useEffect(() => {
    if (!userId || !enabled) return;
    return subscribe(userChannel(userId), {
      onPublication: (data) => {
        const pub = parsePublication(data);
        if (pub?.type === 'activity.new' && pub.agencyId === agencyRef.current) reload();
      },
      onSubscribed: () => reload(),
      onError: () => console.warn('realtime: user channel refused or failed (activity)'),
    });
  }, [userId, enabled, subscribe, reload]);

  const [visible, setVisible] = React.useState(isVisible);
  React.useEffect(() => {
    if (!enabled) return;
    const onChange = () => {
      const now = isVisible();
      setVisible(now);
      if (now) reload();
    };
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, [reload, enabled]);
  const delay = enabled ? pollDelay(rtState, visible) : null;
  React.useEffect(() => {
    if (delay === null) return;
    const id = setInterval(() => void readRef.current(), delay);
    return () => clearInterval(id);
  }, [delay]);

  const markRead = React.useCallback(
    async (untilIso: string) => {
      if (!enabled || !agencyId) return;
      setUnreadCount(0);
      const { error } = await client.rpc('activity_mark_read', { p_agency: agencyId, p_until: untilIso });
      if (error) console.warn('useActivity: activity_mark_read failed', error);
      reload();
    },
    [client, agencyId, enabled, reload],
  );

  return { enabled, status, items, unreadCount, reload, markRead };
}
